import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { AuditResult, Vulnerability } from '../models';

interface AuditResultResponse {
  id: number;
  pageId: number;
  processId?: number;
  pageName: string;
  date: string;
  score: number;
  status: string;
  createdAt: string;
  vulnerabilities: VulnerabilityResponse[];
}

interface VulnerabilityResponse {
  id: number;
  type: string;
  severity: string;
  description: string;
  recommendation: string;
  location?: string;
}

interface AuditRequest {
  pageId: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuditService {
  private apiUrl = '/api/audits';

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getHeaders(): HttpHeaders {
    return this.authService.getAuthHeaders();
  }

  getAuditsByPage(pageId: number): Observable<AuditResult[]> {
    return this.http.get<AuditResultResponse[]>(`${this.apiUrl}/page/${pageId}`, {
      headers: this.getHeaders()
    }).pipe(
      map(responses => responses.map(r => this.mapToAuditResult(r)))
    );
  }

  getAuditById(id: number): Observable<AuditResult> {
    return this.http.get<AuditResultResponse>(`${this.apiUrl}/${id}`, {
      headers: this.getHeaders()
    }).pipe(
      map(response => this.mapToAuditResult(response))
    );
  }

  createAudit(request: AuditRequest): Observable<AuditResult> {
    return this.http.post<AuditResult>(this.apiUrl, request, {
      headers: this.getHeaders()
    });
  }

  deleteAudit(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`, {
      headers: this.getHeaders()
    });
  }

  private mapToAuditResult(response: AuditResultResponse): AuditResult {
    return {
      id: response.id,
      pageId: response.pageId,
      processId: response.processId,
      pageName: response.pageName,
      date: new Date(response.date),
      score: response.score,
      status: response.status as 'completed' | 'in_progress' | 'failed',
      vulnerabilities: response.vulnerabilities.map(v => ({
        id: v.id,
        type: v.type,
        severity: v.severity as 'critical' | 'high' | 'medium' | 'low',
        description: v.description,
        recommendation: v.recommendation,
        location: v.location
      }))
    };
  }
}
